import React from 'react';
import { DialogTitle } from '@mui/material';
import { DateObject } from '@uplift/services';
import { WeekDay, WeekDayKeys, TimeRange } from '@uplift/types';
import { Formik, useFormikContext } from 'formik';
import { useL10n } from '@uplift/hooks';
import { CalendarEventPopover, CalendarEventPopoverProps } from '../ScheduleCalendar/calendar-event-popover';
import { AvailabilityFormSingleDateBodyDesktop } from '../../molecules/Forms/AvailabilityForm';
import { useNormalizeTimes } from './EditingAvailabilityDayPopup.hook';


export type EditingAvailabilityDay = {
  weekday: WeekDayKeys;
  availability: TimeRange[];
  date?: DateObject;
  defaultAvailability?: TimeRange[];
  isAddingOverride?: boolean;
};


export type LiveAvailabilityData = {
  date?: DateObject;
  weekday: WeekDayKeys;
  ranges: TimeRange[];
};


const EditModal: React.FC<{
  open: boolean;
  editingAvailabilityTemplate: EditingAvailabilityDay;
  onClose: () => void;
  warningOnLessThenHour?: boolean;
  anchorElementProps?: {
    anchor: HTMLElement;
    placement: CalendarEventPopoverProps['placement'];
  };
  modalTitle?: React.ReactNode;
  prepend?: React.ReactNode;
  onLiveChange?: (data?: LiveAvailabilityData) => void;
}> = (props) => {
  const formikContext = useFormikContext<{ availability: TimeRange[] }>();
  const onLiveChangeRef = React.useRef(props.onLiveChange);
  const editingTemplateRef = React.useRef(props.editingAvailabilityTemplate);

  onLiveChangeRef.current = props.onLiveChange;
  editingTemplateRef.current = props.editingAvailabilityTemplate;
  const prevAvailabilityRef = React.useRef<typeof formikContext.values.availability>(formikContext.values.availability);

  React.useEffect(() => {
    if (prevAvailabilityRef.current === formikContext.values.availability) {
      return;
    }
    prevAvailabilityRef.current = formikContext.values.availability;
    const onLiveChange = onLiveChangeRef.current;
    const editingAvailabilityTemplate = editingTemplateRef.current;

    if (editingAvailabilityTemplate) {
      onLiveChange?.({
        date: editingAvailabilityTemplate.date,
        weekday: editingAvailabilityTemplate.weekday,
        ranges: formikContext.values.availability.filter(r => r.start && r.end)
      });
    } else {
      onLiveChange?.(undefined);
    }
  }, [ formikContext.values.availability ]);

  React.useEffect(() => () => {
    (onLiveChangeRef.current as any)?.(undefined);
  }, [ props.editingAvailabilityTemplate ]);

  return (
    <CalendarEventPopover
      autoScroll={false}
      open={!!props.editingAvailabilityTemplate}
      onClose={props.onClose}
      {...props.anchorElementProps}
    >
      <div>
        <DialogTitle>
          {props.modalTitle}
        </DialogTitle>
        <AvailabilityFormSingleDateBodyDesktop
          isAddingOverride={props.editingAvailabilityTemplate.isAddingOverride ?? false}
          handleClose={props.onClose}
          warningOnLessThenHour={props.warningOnLessThenHour}
          defaultAvailability={props.editingAvailabilityTemplate.defaultAvailability}
          prepend={props.prepend}
        />
      </div>
    </CalendarEventPopover>
  );
};

export const EditingAvailabilityDayPopup: React.FC<{
  calendarRoot: HTMLDivElement;
  editingAvailabilityTemplate: EditingAvailabilityDay;
  setEditingAvailabilityTemplate: (newT?: EditingAvailabilityDay) => void;
  warningOnLessThenHour?: boolean;
  onAvailabilityChange: (ranges: TimeRange[], date?: DateObject) => Promise<void>;
  prepend?: React.ReactNode;
  onLiveChange?: (data?: LiveAvailabilityData) => void;
}> = ({
  calendarRoot,
  warningOnLessThenHour,
  editingAvailabilityTemplate,
  setEditingAvailabilityTemplate,
  onAvailabilityChange,
  prepend,
  onLiveChange,
}) => {
  const l10n = useL10n();
  const { normalizeTimes } = useNormalizeTimes();
  const modalTitle = React.useMemo(() => {
    if (editingAvailabilityTemplate.date) {
      return `Override availability for ${editingAvailabilityTemplate.date.format('MMM D, YYYY')}`;
    }

    if (editingAvailabilityTemplate) {
      return `${l10n.general.edit} ${l10n.general.weekdayPlural[WeekDay[editingAvailabilityTemplate.weekday]]}`;
    }

    return l10n.general.edit;
  }, [ l10n, editingAvailabilityTemplate ]);

  const anchorElementProps = React.useMemo(() => {
    if (editingAvailabilityTemplate.weekday === undefined) {
      return undefined;
    }

    const daysHead = calendarRoot.querySelectorAll('.fc-col-header-cell.fc-day');

    if (daysHead.length === 1) {
      return {
        anchor: calendarRoot.querySelector('.fc-header-toolbar.fc-toolbar') as HTMLElement,
        placement: 'bottom' as const,
      };
    }

    return {
      anchor: daysHead?.[WeekDay[editingAvailabilityTemplate.weekday]] as HTMLElement,
      placement: 'left-start' as const,
    };
  }, [ editingAvailabilityTemplate.weekday, calendarRoot ]);

  React.useEffect(() => {
    if (editingAvailabilityTemplate.weekday === undefined) return;

    const daysBody = calendarRoot.querySelectorAll('.fc-timegrid-col.fc-day .fc-timegrid-col-bg');
    const dayBody = daysBody?.[WeekDay[editingAvailabilityTemplate.weekday]] as HTMLElement;

    dayBody.style.zIndex = '4';

    return () => {
      dayBody.style.zIndex = '';
    };
  }, [ editingAvailabilityTemplate.weekday, calendarRoot ]);

  return (
    <Formik
      enableReinitialize
      key={editingAvailabilityTemplate.weekday}
      initialValues={{
        availability: editingAvailabilityTemplate.availability.map((a, i) => ({ ...a, id: i })) ?? [],
      }}
      onSubmit={async ({ availability }) => {
        if (!editingAvailabilityTemplate) return;

        const dayRanges = availability.map(r => ({ start: r.start, end: r.end }));
        const normalizedDayRanges = await normalizeTimes({ ranges: dayRanges, squash: true });

        await onAvailabilityChange?.(normalizedDayRanges.ranges, editingAvailabilityTemplate.date);
        setEditingAvailabilityTemplate(undefined);
      }}
    >
      {editingAvailabilityTemplate && anchorElementProps && (
        <EditModal
          open={!!editingAvailabilityTemplate}
          onLiveChange={onLiveChange}
          anchorElementProps={anchorElementProps}
          modalTitle={modalTitle}
          prepend={prepend}
          warningOnLessThenHour={warningOnLessThenHour}
          editingAvailabilityTemplate={editingAvailabilityTemplate}
          onClose={() => setEditingAvailabilityTemplate(undefined)}
        />
      )}
    </Formik>
  );
};
